import { useEffect, useRef } from 'react';

/**
 * Custom hook that returns a debounced version of the provided callback function.
 * Ensures that the function is executed only after the specified delay has passed
 * since the last invocation.
 *
 * @template T - A function type with any number of arguments.
 * @param {T} callback - The function to be debounced.
 * @param {number} delay - The debounce delay in milliseconds.
 * @returns {(...args: Parameters<T>) => void} - A debounced function that delays execution of `callback`.
 *
 * @example
 * ```tsx
 * const handleSearch = (query: string) => {
 *   console.log("Searching for:", query);
 * };
 *
 * const debouncedSearch = useDebounceForFunctions(handleSearch, 300);
 *
 * <input onChange={(e) => debouncedSearch(e.target.value)} />;
 * ```
 *
 * @remarks
 * - Automatically clears any pending timeout when the component unmounts.
 * - Uses `useRef` to store the timeout reference and `useEffect` for cleanup.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Can be passed any function
const useDebounceForFunctions = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const debouncedCallback = (...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };

  return debouncedCallback;
};

// eslint-disable-next-line import/prefer-default-export
export { useDebounceForFunctions };
